import { MeiliSearch, SearchParams } from "meilisearch"
import { clickOutside, dateLocaleString } from "../auth/helpers"

import styles from "./global-search.scss?inline"
import templateHTML from './global-search.component.html?raw'
import { i18n, LANGUAGES } from "../core/services/i18n.service"

export class GlobalSearchComponent extends HTMLElement {
  private _sort = ["createdOn:desc"]
  private _client: MeiliSearch

  private _searchInput: HTMLInputElement
  private _hitList: HTMLOListElement
  private _closeButton: HTMLButtonElement
  private _searchTitle: HTMLDivElement

  constructor() {
    super()
    this.attachShadow({ mode: "open" })
    const templateContainer = document.createElement('div');
    templateContainer.innerHTML = templateHTML;
    const template = templateContainer.querySelector('template');
    
    // Create and append style elements
    const style = document.createElement('style');
    style.textContent = styles;
    this.shadowRoot.appendChild(style);
    
    this.shadowRoot.appendChild(template.content.cloneNode(true))
  }

  get dialog() {
    return this.shadowRoot.querySelector<HTMLDialogElement>('dialog')
  }

  inputHandler(e: InputEvent) {
    if (!(e.target as HTMLInputElement).value) {
      this.search((e.target as HTMLInputElement).value)
      this._searchTitle.innerText = i18n.t("searchTitle")
    }
    else {
      this.search((e.target as HTMLInputElement).value, { limit: 10, sort: this._sort })
      this._searchTitle.innerText = i18n.t("searchTitleResult")
    }
  }

  clickHandler(e: MouseEvent) {
    if (clickOutside(e)) { this.close() }
  }

  async connectedCallback() {
    this._searchInput = this.shadowRoot.querySelector<HTMLInputElement>("input[type=search]")
    this._hitList = this.shadowRoot.querySelector<HTMLOListElement>(".hit-list")
    this._closeButton = this.shadowRoot.querySelector<HTMLButtonElement>(".close-btn")
    this._searchTitle = this.shadowRoot.querySelector<HTMLDivElement>("#search-title")
    // @ts-ignore
    const [host, apiKey] = [import.meta.env.VITE_MEILI_ENDPOINT, import.meta.env.VITE_MEILI_KEY]
    if (!host || !apiKey) { throw new Error("missing search env") }

    this._client = new MeiliSearch({ host, apiKey })
    this._searchInput.placeholder = i18n.t("searchPlaceholder")

    this._searchInput.addEventListener("input", this.inputHandler.bind(this))
    this._closeButton.addEventListener("click", this.close.bind(this))
    this._searchTitle.innerText = i18n.t("searchTitle")
    this.shadowRoot.addEventListener("click", this.clickHandler.bind(this))
    this.shadowRoot.querySelectorAll<HTMLDivElement>(".no-result-container *[i18n]").forEach((elem: HTMLElement) => elem.innerText = i18n.t(elem.attributes.getNamedItem("i18n").value))
    this.search('')
  }

  disconnectedCallback() {
    this._searchInput.removeEventListener("input", this.inputHandler.bind(this))
    this._closeButton.addEventListener("click", this.close.bind(this))
    this.removeEventListener("click", this.clickHandler)
  }

  show() {
    this.dialog.showModal()
    document.body.style.overflow = 'hidden'
  }

  close() { 
    this.dialog.close()
    document.body.style.overflow = ''
  }

  hitToView(hit: any) {
    const href = i18n.getLanguage() === LANGUAGES.FR ? `/article/${hit.slug}` : `/article-en/${hit.slug}`
    const dateHTML = (date: number) =>
      `
      <div class="hit-item-date">
        <svg width="13" height="12" viewBox="0 0 13 12" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M4.5 0.999878V2.49988" stroke="var(--midnight-blue)" stroke-width="0.74211" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M8.5 0.999878V2.49988" stroke="var(--midnight-blue)" stroke-width="0.74211" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M2.25 4.54492H10.75" stroke="var(--midnight-blue)" stroke-width="1.07194" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M11 4.24988V8.49988C11 9.99988 10.25 10.9999 8.5 10.9999H4.5C2.75 10.9999 2 9.99988 2 8.49988V4.24988C2 2.74988 2.75 1.74988 4.5 1.74988H8.5C10.25 1.74988 11 2.74988 11 4.24988Z" stroke="var(--midnight-blue)" stroke-width="1.07194" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <div class="caption1">${dateLocaleString(date)}</div>
      </div>
    `
    const date = hit.createdOn

    return `
      <li class="hit-item">
        <div class="hit-item-content">
          <div class="hit-item-thumbnail">
            <img src=${hit.thumbnail.url}>
          </div>
          <a class="hit-item-link body3" href=${href}>${hit.name}</a>
        </div>
        ${date ? dateHTML(date) : ""}
        <a class="hit-item-see caption1" href=${href}>${i18n.t("see")}</a>
      </li>`
  }

  async search(query: string, options: SearchParams = { limit: 6, sort: this._sort }): Promise<void> {
    const lang = i18n.getLanguage()
    let index = lang === LANGUAGES.FR ? "article" : "article-en"
    const res = await this._client.index(index).search(query, options)

    if (res.hits.length > 0) { 
      this.dialog.classList.remove("no-result") 
    } else { 
      this.dialog.classList.add("no-result") 
    }
    this._hitList.innerHTML = res.hits.map(this.hitToView).join('')
  }
}
customElements.define("tbw-global-search", GlobalSearchComponent)

